import { z } from 'zod';
import { ExperienceType } from '../../../../enums/experience';

const HiringDetailsBaseSchema = z.object({
    minHireHours: z.number(),
    maxHireHours: z.number(),
    bufferHours: z.coerce
        .number({ message: 'Required' })
        .int({ message: 'Must be a whole number' })
        .min(0, { message: 'Cleaning buffer cannot be negative' }),
    checkinTime: z.string().min(1, { message: 'Required' }),
    checkoutTime: z.string().min(1, { message: 'Required' }),
    monday: z.boolean(),
    tuesday: z.boolean(),
    wednesday: z.boolean(),
    thursday: z.boolean(),
    friday: z.boolean(),
    saturday: z.boolean(),
    sunday: z.boolean(),
    publicHolidays: z.boolean(),
    requiresBookingApproval: z.boolean(),
    autoApprovalGuests: z.array(z.object({ id: z.number() })),
});

const validateHiringDetails = (
    val: z.infer<typeof HiringDetailsBaseSchema>,
    ctx: z.RefinementCtx,
) => {
    if (val.minHireHours > val.maxHireHours) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Min hire period must be less than max hire period',
            path: ['minHireHours'],
        });
    }

    if (val.maxHireHours < val.minHireHours) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Max hire period must be greater than min hire Period',
            path: ['maxHireHours'],
        });
    }

    if (
        !val.monday &&
        !val.tuesday &&
        !val.wednesday &&
        !val.thursday &&
        !val.friday &&
        !val.saturday &&
        !val.sunday
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one day required',
            path: ['monday'],
        });
    }
};

export const HiringDetailsSchema = HiringDetailsBaseSchema.superRefine(
    validateHiringDetails,
);

export const PropertyDetailsSchema = HiringDetailsBaseSchema.extend({
    halfDayCheckoutTime: z.string().optional().nullable(),
    halfDayCheckinTime: z.string().optional().nullable(),
}).superRefine((val, ctx) => {
    validateHiringDetails(val, ctx);

    if (val.minHireHours < 24) {
        if (!val.halfDayCheckoutTime) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['halfDayCheckoutTime'],
            });
        }
        if (!val.halfDayCheckinTime) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Required',
                path: ['halfDayCheckinTime'],
            });
        }
    }
});

export const VehicleHiringDetailsSchema = HiringDetailsBaseSchema.extend({
    details: z.object({
        minimumLicenceRequired: z.enum(['full', 'p2', 'p1']),
        minimumDriversAge: z.number().optional().nullable(),
        petsAllowed: z.boolean(),
        smokingAllowed: z.boolean(),
    }),
}).superRefine(validateHiringDetails);

export const getAvailabilitySchema = (type: ExperienceType) => {
    switch (type) {
        case ExperienceType.Commercial:
        case ExperienceType.Property:
            return PropertyDetailsSchema;
        case ExperienceType.Vehicle:
            return VehicleHiringDetailsSchema;
        default:
            return HiringDetailsSchema;
    }
};
